import styled from 'styled-components';
import { motion } from 'framer-motion';

// Main Layout Container
export const PageContainer = styled(motion.div)`
  width: 100%; /* Full width to eliminate side gaps */
  min-height: 100%; /* Fill available space */
  display: block;
  padding: 0; /* No padding on mobile for full screen */
  margin: 0;
  position: relative;

  @media (min-width: 1200px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.lg};
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

// Header Section
export const HeaderSection = styled(motion.section)`
  text-align: center;
  width: 100%;
  padding: 0;

  @media (min-width: 1200px) {
    max-width: 600px;
  }
`;

// Main Content Card
export const MainCard = styled(motion.div)`
  width: 100%; /* Full width horizontally */
  min-height: auto; /* Natural height - expands with content */
  background: ${({ theme }) => theme.colors.gradient.card};
  backdrop-filter: blur(20px);
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
  position: relative;
  margin: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0; /* Rounded top corners on mobile */
  box-shadow: ${({ theme }) => theme.shadows.card};
  text-align: center;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  /* Gradient top border decoration - matches BaseModal */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
    z-index: 1;
  }

  /* Mobile optimizations - match BaseModal Container */
  @media (max-width: 767px) {
    padding: 24px 20px; /* Match BaseModal padding */
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }

  @media (max-width: 480px) {
    padding: 20px 16px; /* Match BaseModal smaller screen padding */
    border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  }

  @media (min-width: 1200px) {
    background: ${({ theme }) => theme.colors.gradient.card};
    border-radius: ${({ theme }) => theme.borderRadius.xl}; /* All corners rounded on desktop */
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: ${({ theme }) => theme.spacing.xxl};
    max-width: 600px;
    min-height: auto;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }
`;

// Card Sections
export const CardSection = styled(motion.section)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Section Headers
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

export const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

// Form Grid
export const FormGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
  grid-template-columns: 1fr;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

// Input Group
export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

// Action Section
export const ActionSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-top: ${({ $noBorder }) => $noBorder ? '0' : ({ theme }) => theme.spacing.xl};
  padding-top: ${({ $noBorder }) => $noBorder ? '0' : ({ theme }) => theme.spacing.lg};
  border-top: ${({ $noBorder }) => $noBorder ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
`;

// Footer Section
export const FooterSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

// Status Cards
export const StatusCard = styled(motion.div)`
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'expired': return 'rgba(255, 76, 76, 0.1)';
      case 'urgent': return 'rgba(255, 107, 53, 0.1)';
      case 'warning': return 'rgba(255, 165, 0, 0.1)';
      default: return 'rgba(0, 255, 133, 0.1)';
    }
  }};
  border: 1px solid ${({ $status, theme }) => {
    switch ($status) {
      case 'expired': return 'rgba(255, 76, 76, 0.3)';
      case 'urgent': return 'rgba(255, 107, 53, 0.3)';
      case 'warning': return 'rgba(255, 165, 0, 0.3)';
      default: return 'rgba(0, 255, 133, 0.3)';
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

// Info Grid
export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

// Info Item
export const InfoItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export const InfoLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const InfoValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

// Responsive Utilities
export const MobileOnly = styled.div`
  display: block;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const DesktopOnly = styled.div`
  display: none;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;
